
import React from "react";
import { Headphones, Eye, FolderArchive, BrainCircuit, MapPin, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface PerceptiveSystemIconProps {
  diceValue: number;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  className?: string;
}

const getSystemName = (diceValue: number): string => {
  switch (diceValue) {
    case 1: return "Sound";
    case 2: return "Physical";
    case 3: return "Movement";
    case 4: return "Thought";
    case 5: return "Space";
    case 6: return "Voice";
    default: return "Unknown";
  }
};

const PerceptiveSystemIcon: React.FC<PerceptiveSystemIconProps> = ({ 
  diceValue, 
  size = "md", 
  showLabel = false,
  className
}) => {
  const iconSizeClass = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-12 w-12",
  };

  const containerSizeClass = {
    sm: "w-10 h-10",
    md: "w-14 h-14",
    lg: "w-20 h-20",
  };

  const labelSizeClass = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  // Render the appropriate icon based on the dice value
  const renderIcon = () => {
    switch (diceValue) {
      case 1:
        return <Headphones className={iconSizeClass[size]} />;
      case 2:
        return <Eye className={iconSizeClass[size]} />;
      case 3:
        return <FolderArchive className={iconSizeClass[size]} />;
      case 4:
        return <BrainCircuit className={iconSizeClass[size]} />;
      case 5:
        return <MapPin className={iconSizeClass[size]} />;
      case 6:
        return <MessageCircle className={iconSizeClass[size]} />;
      default:
        return <div className={iconSizeClass[size]}>?</div>;
    }
  };

  return (
    <div className={cn("flex flex-col items-center", className)}>
      <div className={cn(
        containerSizeClass[size],
        "flex items-center justify-center rounded-full bg-primary/20 text-primary",
      )}>
        {renderIcon()}
      </div>
      {showLabel && (
        <span className={cn(
          "mt-1 font-medium text-center",
          labelSizeClass[size]
        )}>
          {getSystemName(diceValue)}
        </span>
      )}
    </div>
  );
};

export default PerceptiveSystemIcon;
